import React from "react";
import {
  Box,
  EntitySelectorContent,
  EntitySelectorFooter,
  EntitySelectorFooterAction,
  EntitySelectorInput,
  EntitySelectorPool,
  Skeleton,
  Text,
  useEntitySelector,
} from "@salestrak/ui";
import { formatPhoneNumber } from "@salestrak/custom-fields";
import {
  SelectionEntityTrigger,
  UserSelector,
  selectionToUserEntity,
} from "#app/components/entity-selectors";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { DetailFieldRow } from "#app/components/entity/detail-field-row";
import { EntityGroupTag } from "#app/components/entity/entity-group";
import { EntityTag } from "#app/components/entity/entity-tag";
import { RowAction } from "#app/components/entity/row-actions";
import {
  useMemberDirectory,
  useMemberEntityResolver,
} from "#app/lib/data/member-directory-provider";
import { teamRefToEntity } from "#app/lib/entity-visuals";
import { useSetReportsTo } from "#app/lib/hooks/use-set-reports-to";
import { isFormerMember } from "#app/lib/role-utils";
import type { KnownMember, MemberDetail } from "./types";
import { memberContact } from "../member-contact";

/** The picker's "none" row: shown only while a manager is set, it clears the
 *  reporting line and closes the popover — the same result as the trigger's
 *  hover ×, reachable from inside the list for anyone who opened it to
 *  change the value and decided on no manager instead. */
function ReportsToNoneAction({ onSelect }: { onSelect: () => void }) {
  const { selections, onOpenChange } = useEntitySelector();
  if (selections.length === 0) return null;
  return (
    <EntitySelectorFooter>
      <EntitySelectorFooterAction
        icon="x"
        onClick={() => {
          onSelect();
          onOpenChange(false);
        }}
      >
        No manager
      </EntitySelectorFooterAction>
    </EntitySelectorFooter>
  );
}

/**
 * The reporting line — a real User field. The UpdateMember mutation returns
 * managerId, so Apollo folds the new value back into the cached member.
 */
function ReportsToRow({
  userId,
  managerId,
  canEdit,
  onViewMember,
}: {
  userId: string | null;
  managerId: string | null;
  /** The reporting line is an org fact with one owner: manage.users. */
  canEdit: boolean;
  onViewMember?: ((userId: string) => void) | undefined;
}) {
  const resolveMember = useMemberEntityResolver();
  const { pendingOf } = useMemberDirectory();
  const setReportsTo = useSetReportsTo("profile");
  const setManagerId = React.useCallback(
    (id: string | null) => {
      if (userId) void setReportsTo(userId, id);
    },
    [userId, setReportsTo],
  );

  // Resolve the selected manager to an entity so the row can show their
  // avatar and link to their profile — through the member directory, which
  // answers managers outside the bootstrap roster page.
  const managerEntity = managerId ? resolveMember(managerId) : null;
  // Only the member themself is withheld from the picker. Someone from their
  // own reporting line IS offered: picking them lifts that person up into
  // this member's spot (see useSetReportsTo).
  const managerExclude = React.useMemo(
    () => (userId ? [userId] : undefined),
    [userId],
  );
  const managerFormer =
    managerEntity?.kind === "user" && isFormerMember(managerEntity.user);

  return (
    <DetailFieldRow
      // A manager who is suspended or has left is not in the picker's pool
      // (it lists active and invited members), so the value falls back to
      // the plain link with the clear action beside it — clearing is the one
      // edit that still applies.
      actions={
        managerEntity && managerFormer && canEdit ? (
          <RowAction icon="x" label="Clear" onClick={() => setManagerId(null)} />
        ) : undefined
      }
      align="center"
      hideLabel
      icon="user-lock"
      label="Reports to"
      leadingWidth={24}
    >
      {canEdit && !managerFormer ? (
        // The value IS the picker: pressing it reopens the popover to pick
        // someone else, the hover × clears, the hover arrow opens the
        // manager's profile. The 24px squared ghost rung bleeds start-ward by
        // its 8px text inset so the value aligns with the sibling rows.
        <Box
          align="center"
          alignSelf="start"
          direction="row"
          marginLeftNegative="sm"
          minWidth={0}
          shrink
        >
          <UserSelector
            title="Reports to"
            exclude={managerExclude}
            maxSelections={1}
            onValueChange={(ids) => setManagerId(ids[0] ?? null)}
            value={managerId}
          >
            <SelectionEntityTrigger
              onClear={() => setManagerId(null)}
              onOpen={managerId ? () => onViewMember?.(managerId) : undefined}
              openLabel="View profile"
              placeholder="Set reports-to"
              shape="square"
              showChevron
              size="xs"
              title="Reports to"
              toEntity={selectionToUserEntity}
              valueDisplay="label"
              valuePrefix="Reports to"
            />
            <EntitySelectorContent>
              <EntitySelectorInput />
              <EntitySelectorPool value={USER_POOL_ID} />
              <ReportsToNoneAction onSelect={() => setManagerId(null)} />
            </EntitySelectorContent>
          </UserSelector>
        </Box>
      ) : managerEntity ? (
        // Read-only: the value is a sentence, not a control, so it wears no
        // ring or wash — a viewer who cannot change the manager gets nothing
        // that looks like a picker. The name is the inline chip (underline +
        // preview on hover); pressing it swaps the island to their profile.
        <Text size="sm" truncate={1} variant={managerFormer ? "muted" : undefined}>
          Reports to{" "}
          <EntityTag
            as="text"
            display="label"
            entity={managerEntity}
            labelInk={managerFormer ? "muted" : undefined}
            onClick={() => onViewMember?.(managerEntity.id)}
            size="sm"
            underlineHover
            weight="regular"
          />
        </Text>
      ) : managerId && pendingOf(managerId) ? (
        <Skeleton delayed height={14} width={120} />
      ) : (
        <Text size="sm" variant="muted">
          No manager
        </Text>
      )}
    </DetailFieldRow>
  );
}

/**
 * Property rows — the Overview tab's fields. Callers own the wrapping column
 * + paddings.
 */
export function MemberProfileFields({
  userId,
  known,
  user,
  initialLoading,
  canEditReportsTo,
  onViewMember,
}: {
  userId: string | null;
  known: KnownMember | null;
  user: MemberDetail | null;
  /** The first fetch is in flight and nothing is cached for this member. */
  initialLoading: boolean;
  canEditReportsTo: boolean;
  onViewMember?: ((userId: string) => void) | undefined;
}) {
  const teams = user?.teams ?? [];
  return (
    <>
      <DetailFieldRow
        copyable
        hideLabel
        icon="circle-user"
        label="Username"
        leadingWidth={24}
        onContact={memberContact("username", "profile")}
        truncate={1}
        value={known?.username}
      />
      <DetailFieldRow
        hideLabel
        icon="mail"
        label="Email"
        leadingWidth={24}
        link="email"
        onContact={memberContact("email", "profile")}
        truncate={1}
        value={known?.email}
      />
      <ReportsToRow
        canEdit={canEditReportsTo}
        managerId={known?.managerId ?? null}
        onViewMember={onViewMember}
        userId={userId}
      />
      {/* Phone and teams are the two values the roster does not carry: the
          row renders real with a delayed bar standing in for the value. */}
      <DetailFieldRow
        hideLabel
        icon="phone"
        label="Phone"
        leadingWidth={24}
        link="phone"
        onContact={memberContact("phone", "profile")}
        value={user?.phoneNumber ? formatPhoneNumber(user.phoneNumber) : user?.phoneNumber}
      >
        {initialLoading ? <Skeleton delayed height={14} width={120} /> : undefined}
      </DetailFieldRow>
      <DetailFieldRow hideLabel icon="users" label="Teams" leadingWidth={24}>
        {/* List-view team cell rule: a single team reads as tile + name; 2+
            collapse to the square stack — never the tinted expanded pill. */}
        {initialLoading ? (
          <Skeleton delayed height={16} width={96} />
        ) : teams.length === 1 ? (
          <EntityTag entity={teamRefToEntity(teams[0]!)} weight="regular" />
        ) : teams.length > 1 ? (
          <EntityGroupTag
            entities={teams.map(teamRefToEntity)}
            size="xs"
            title="Teams"
            variant="stack"
          />
        ) : (
          <Text size="sm" variant="muted">
            No teams
          </Text>
        )}
      </DetailFieldRow>
    </>
  );
}
